/**
 * Slugs — the ONE home for every anchor and URL segment the docs make from words.
 *
 * Read by both sides: the build script (heading anchors in the "On this page" list, operation and
 * category URL segments) and the site (the `id` a rendered heading gets). Same function on both
 * sides, so a TOC link always lands on its heading. Known form: GitHub's heading anchors
 * (`github-slugger`): lower-case, punctuation dropped, spaces become hyphens.
 */

/** A heading's anchor or a URL segment: "Event log (`?logs=1`)" → "event-log-logs1". */
export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,./:;<=>?@[\]^`{|}~\u2019]/g, "")
    .replace(/\s/g, "-");

/** Anchors unique within one page: the second "Example" becomes "example-1" (github-slugger's rule). */
export const createSlugger = () => {
  const seen = new Map<string, number>();

  return (text: string): string => {
    const base = slugify(text);
    const count = seen.get(base);
    if (count === undefined) {
      seen.set(base, 0);

      return base;
    }
    seen.set(base, count + 1);

    return `${base}-${count + 1}`;
  };
};

/** A field anchor (design §4.5: `#param-…`, `#body-…`, `#response-…`); a path's dots and brackets stay readable. */
export const fieldAnchor = (section: "param" | "body" | "response", path: string): string => `${section}-${path.replace(/\[\]/g, "").replace(/[^A-Za-z0-9_.-]/g, "-")}`;

/** The plain text of a Markdown (mdast) node — what a reader sees in a heading, so what its anchor is made from. */
export interface TextNode {
  type: string;
  value?: string;
  children?: TextNode[];
}

export const plainText = (node: TextNode): string =>
  typeof node.value === "string" && (node.type === "text" || node.type === "inlineCode") ? node.value : (node.children ?? []).map(plainText).join("");
